import type { TemplateInfo, IProviderConfig } from '~/types/template';

import { isTemplateShopifyBackup, isTemplateShopifyOrigin } from './templateChecker';
import { capitalizeFirstLetter } from './convertPathnameToTitle';
import { getProviderConfig } from './templateHelper';
import { TemplateType } from '~/types/template';

export const parseLabelTemplate = (template?: TemplateInfo): string => {
  if (!template) return 'Unknown';

  if (isTemplateShopifyOrigin(template.providerSource)) {
    return getBaseLabelByTemplateType(template.suffix) ?? template.suffix;
  }
  if (isTemplateShopifyBackup(template.providerSource)) {
    return `Shopify Origin ${capitalizeFirstLetter(template.type)} Live Theme`;
  }
  const providerConfig = getProviderConfig(template.providerSource);
  if (providerConfig && providerConfig.prefix && providerConfig.isMapping) {
    return formatLabelByProvider(providerConfig, template.suffix);
  }

  return template.suffix;
};

export const getBaseLabelByTemplateType = (templateType?: string): string | undefined => {
  if (!templateType) return;

  const labelTemplate = (text: string) => `Default ${text} Template`;
  switch (templateType) {
    case TemplateType.HOME:
      return labelTemplate('Home');
    case TemplateType.ARTICLE:
      return labelTemplate('Blog Post');
    case TemplateType.COLLECTION:
      return labelTemplate('Collection');
    case TemplateType.PRODUCT:
      return labelTemplate('Product');
    case TemplateType.PAGE:
      return labelTemplate('Page');
  }
};

const formatLabelByProvider = (providerConfig: IProviderConfig, templateSuffix: string): string => {
  const cleanedSuffix = removePrefix(templateSuffix, providerConfig.prefix!);
  const match = cleanedSuffix.match(/(.+?)(-\d+)?$/);
  const suffixNumber = match?.[2] ? match[2].replace('-', '') : '';
  const suffix = match?.[1];
  const baseLabel = getBaseLabelByTemplateType(suffix);
  const _copyLabel = baseLabel ? `Copy ${baseLabel} ${suffixNumber}` : templateSuffix;

  return `[${providerConfig.label}] ${templateSuffix}`.trim();
};

const removePrefix = (text: string, prefix: string): string => {
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
};

export const getLabelByTemplateType = (
  templateType?: TemplateType,
  options?: {
    lean?: boolean;
    total?: number;
    capitalize?: boolean;
  },
): string => {
  if (!templateType) return '';

  let text = '';
  switch (templateType) {
    case TemplateType.HOME:
      text = 'home';
      break;
    case TemplateType.ARTICLE:
      text = options?.lean ? 'post' : 'blog post';
      break;
    case TemplateType.COLLECTION:
      text = 'collection';
      break;
    case TemplateType.PRODUCT:
      text = 'product';
      break;
    case TemplateType.PAGE:
      text = 'page';
      break;
  }

  if (options?.total) {
    text = genTextByTotal(text, options.total);
  }

  if (options?.capitalize) {
    text = capitalizeFirstLetter(text);
  }

  return text;
};

export const genTextByTotal = (text: string, total: number): string => {
  return total > 1 ? `${text}s` : text;
};
